Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

<template>
  <div class="container">
    <div class="sign-in">
      <p>{{ $t("signUp.alreadyHaveAnAccount") }}</p>
      <router-button-comp
        id="sign-in"
        legacySecondary
        :to="signInUrl">
        {{ $t("signUp.signIn") }}
      </router-button-comp>
    </div>
    <h1>{{ $t("signUp.letsGetStarted") }}</h1>
    <div>
      <vue-recaptcha
        v-if="recaptchaEnabled"
        ref="invisibleRecaptcha"
        @verify="completeSignUp"
        size="invisible"
        :sitekey="recaptchaKey"
        :loadRecaptchaScript="true"
        recaptchaHost="www.recaptcha.net"
        :language="$i18n.locale"
      >
      </vue-recaptcha>
      <profile-info
        :model="formData"
        :validateOn="validateOn"
        ref="profileInfo"
      >
        <template #submitButton>
          <button-comp
            id="submitButton"
            @click="signUp"
            type="submit"
            legacyOrange
            large
          >
            {{ $t("signUp.continue") }}
          </button-comp>
        </template>
      </profile-info>
    </div>
    <p class="by-clicking-continue">
      {{ $t("signUp.byClickingContinue") }}
      <a id="showTermsLink" @click="showTerms">{{
        $t("signUp.termsOfUse")
      }}</a>
    </p>
  </div>
</template>

<script lang="ts">
import { mapGetters, mapState } from 'vuex';
import profileInfo from '@/components/auth/profile-info.vue';
import {
  postSignUpAsync,
  postSignInAsync,
  userFailures,
} from '@/api/userApi';
import { isSuccess, isError } from '@/utils/result';
import { ProfileInfo } from '@/models/models';
import { getterTypes } from '@/store/geolocation';
import { VueRecaptcha } from 'vue-recaptcha';
import RecaptchaSettings from '@/RecaptchaSettings';
import analytics from '@/utils/analytics';
import { IState } from '@/store';
import { ValidateOn } from '@protolabs/component-library/src/interfaces/customValidation';
import { useHeaderComposable } from '@/composables/headerComposable';

export default {
  setup() {
    const headerComposable = useHeaderComposable();

    headerComposable.createHeaderLinks('signup');
  },
  name: 'signup-view',
  components: {
    // eslint-disable-next-line vue/no-unused-components
    profileInfo,
    VueRecaptcha,
  },
  data() {
    return {
      formData: new ProfileInfo(),
      validateOn: 'submit' as ValidateOn,
    };
  },
  computed: {
    ...mapState({
      countries: (state: any) => (state as IState).geolocation.countries,
      linkGroups: (state: any) => (state as IState).geolocation.linkGroups,
    }),
    ...mapGetters({
      termsLinkByCountryCode: getterTypes.SALE_TERMS_LINK,
      countryByCountryCode: getterTypes.COUNTRY_BY_CODE,
    }),
    selectedCountryCode() {
      let country = this.countryByCountryCode(this.formData.countryCode);
      if (country === null) {
        country = this.countries.find((x) => x.code === 'US');
      }
      return country.code;
    },
    locale() {
      if (this.formData.locale) {
        return this.formData.locale;
      }
      if (this.$i18n && this.$i18n.locale) {
        return this.$i18n.locale;
      }
      return navigator.language;
    },
    targetCountryCodeForLinks() {
      const locale = this.locale.split('-');

      if (locale.length === 2) {
        return locale[1];
      }

      return this.selectedCountryCode;
    },
    termsLink() {
      return this.termsLinkByCountryCode(this.targetCountryCodeForLinks);
    },
    query() {
      if (this.$route && this.$route.query) {
        return this.$route.query;
      }
      return {};
    },
    signInUrl() {
      return {
        name: 'Sign In',
        path: '/',
        query: this.query,
      };
    },
    recaptchaKey() {
      return RecaptchaSettings.apiKey;
    },
    recaptchaEnabled() {
      return RecaptchaSettings.isEnabled;
    },
  },
  methods: {
    async signUp() {
      const profileInfoRef = this.$refs.profileInfo as typeof profileInfo;

      if (!profileInfoRef) {
        return;
      }
      this.validateOn = 'blur';
      const isValid = await profileInfoRef.validate();

      if (!isValid) {
        return;
      }

      if (RecaptchaSettings.isEnabled) {
        const invisibleRecaptchRef = this.$refs.invisibleRecaptcha as typeof VueRecaptcha;

        if (!invisibleRecaptchRef) {
          return;
        }

        invisibleRecaptchRef.execute();
      } else {
        await this.completeSignUp('');
      }
    },
    async completeSignUp(recaptchaResponse: any) {
      const profileRef = this.$refs.profileInfo as typeof profileInfo;

      if (!profileRef) {
        return;
      }

      const invisibleRecaptchRef = this.$refs.invisibleRecaptcha as typeof VueRecaptcha;

      if (RecaptchaSettings?.isEnabled && !invisibleRecaptchRef) {
        return;
      }

      this.formData.captchaToken = recaptchaResponse;

      const response = await postSignUpAsync(this.formData);

      if (isError(response)) {
        if (response.message === userFailures.EmailAlreadyExists) {
          profileRef.addEmailValidationError(this.$t('signUp.emailAlreadyExists'));
        } else {
          // TODO: respond to error
        }

        if (RecaptchaSettings?.isEnabled && invisibleRecaptchRef?.reset) {
          invisibleRecaptchRef.reset();
        }

        return;
      }

      this.trackEvent('plEvent:create-account', response);
      const signInResponse = await postSignInAsync({
        email: this.formData.email,
        password: this.formData.password,
        returnUrl: this.query.returnUrl as string,
      });

      if (RecaptchaSettings.isEnabled) {
        invisibleRecaptchRef.reset();
      }

      if (isSuccess(signInResponse)) {
        this.trackEvent('Sign In Completed', response);
        (window as Window).location = signInResponse.returnUrl;
      } else {
        // TODO handle failures
      }
    },
    trackEvent(eventName: string, ecomUserId: string) {
      analytics.track(eventName, {
        email: this.formData.email,
        title: this.formData.title,
        firstName: this.formData.firstName,
        lastName: this.formData.lastName,
        country: this.formData.countryCode,
        region: this.countryByCountryCode(this.formData.countryCode)
          .region,
        phone: this.formData.phoneNumber,
        postalCode: this.formData.postalCode,
        ecomLanguage: this.formData.locale,
        ecomUserId: ecomUserId,
      });
    },
    showTerms() {
      if (this.termsLink) {
        window.open(this.termsLink);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  @include font-header-medium-h1;
  color: $primary-gray;
}

.container {
  max-width: 920px;
  padding-top: 90px;
  padding-left: 40px;
  a {
    color: $brand-blue;
    cursor: pointer;
  }
}

.sign-in {
  @include font-body-book-regular;
  display: flex;
  align-items: center;
  position: absolute;
  top: 50px;
  right: 20px;
  margin: 0;
  color: $primary-gray;

  :first-child {
    margin-right: 20px;
  }
}

:deep(form) {
    max-width: 600px;
}

.learn-why {
  @include font-body-book-regular;
  flex-basis: 100%;
  color: $primary-gray;

  &__button {
    display: inline-block !important;
  }
}

.by-clicking-continue {
  @include font-body-book-regular;
  color: $primary-gray;
  margin-top: 20px;
}

#submitButton {
  margin-top: 40px;
}
</style>
